/**
 * BaaS API console — instance access info, scopes, API keys, webhooks, metrics, logs.
 *
 * Prefer BaaS via `/v1/ib/console/v1/instances/{id}/…` when portal routes are published.
 * Fall back to MobileService `/v1/ib/baas/instances/{id}/…` (Prod SQL) when BaaS 404s —
 * except HML lane-scoped telemetry/webhooks (never read Prod SQL for a development card).
 *
 * Writes (create/revoke/toggle/test) always go through console → BaaS.
 * New partner keys inherit the instance ApiPartner scopes (not per-key scopes).
 */

import { resolveBaasProxyContext, baasProxy, mobileConsoleRequest } from "./baasConsoleClient";
import { isValutSegregationWalletInstance } from "./baasAccountsListApi";
import { isBaasHmlInstance } from "./baasInstancesApi";

/** Public BaaS API docs (override via REACT_APP_BAAS_DOCS_URL if needed). */
export const BAAS_API_DOCS_URL =
  (typeof process !== "undefined" && process.env?.REACT_APP_BAAS_DOCS_URL?.trim()) ||
  "https://docs.valut.io";

/** Public partner gateway (Front Door). Never the App Service WAF-bypass origin. */
export const BAAS_API_PUBLIC_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_BAAS_PUBLIC_URL?.trim()) ||
  "https://api.valut.services/baas"
).replace(/\/+$/, "");

const normalizeBaasBaseUrl = (url) => String(url || "").trim().replace(/\/+$/, "");

const isLoopbackBaasHost = (hostname) => {
  const host = String(hostname || "").trim().toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host === "::1";
};

/**
 * Partner-facing API base for console display. Strips App Service hosts leaked from
 * `Baas__BaseUrl` / instance detail when MS proxies via `*.azurewebsites.net`.
 * @param {string | null | undefined} url
 * @returns {string}
 */
export const toPublicBaasApiBaseUrl = (url) => {
  const candidate = normalizeBaasBaseUrl(url);
  if (!candidate) return BAAS_API_PUBLIC_BASE_URL;

  try {
    const parsed = new URL(candidate);
    if (isLoopbackBaasHost(parsed.hostname)) return candidate;
    if (/\.azurewebsites\.net$/i.test(parsed.hostname)) return BAAS_API_PUBLIC_BASE_URL;
    return candidate;
  } catch {
    return BAAS_API_PUBLIC_BASE_URL;
  }
};

/**
 * Metrics/logs tabs are always shown. Data comes from BaaS when available,
 * otherwise MobileService Prod SQL aggregates (real zeros when no traffic).
 */
export const BAAS_API_METRICS_SUPPORTED = true;

/**
 * Catalog of common partner scopes shown when granted scopes are unavailable.
 * Prefer the live instance grant list when creating keys.
 */
const API_KEY_SCOPES = [
  "accounts.read",
  "accounts.create",
  "transfers.write",
  "fx.quotes.create",
  "fx.conversions.create",
  "fiat.markets.read",
  "entities.list",
  "entities.read",
  "pix.keys.read",
  "pix.payments.write",
  "pix.limits.read",
  "stables.wallets.read",
  "stables.transactions.read",
  "stables.limits.write",
  "instances.read",
  "instances.scopes.read",
  "instances.api_keys.read",
  "instances.webhooks.read",
  "instances.api_metrics.read",
  "instances.api_logs.read",
];

/** Matches BaaS WebhookEvents catalog (Type = baas) used by portal create. */
const WEBHOOK_EVENTS = [
  "account.created",
  "account.status.updated",
  "balance.created",
  "entity.created",
  "entity.updated",
  "entity.verification.updated",
  "entity.verified",
  "pix.payment.sent",
  "pix.payment.receive",
  "pix.payment.confirmed",
  "pix.payment.failed",
  "pix.payment.batch.processed",
  "pix.payment.refunded",
  "pix.payment.chargeback",
  "pix.payment.chargeback.failed",
  "transfer.internal.created",
  "transfer.internal.completed",
  "transfer.internal.failed",
  "stables.transaction.updated",
  "card.program.created",
  "card.program.status.updated",
  "card.issuer_account.created",
  "card.issuer_account.status.updated",
  "card.issuer_account.credit_limit.updated",
  "card.physical.created",
  "card.physical.status.updated",
  "card.funding.received",
  "card.funding.rejected",
  "card.funding.unallocated",
];

export const getBaasApiScopeOptions = (grantedScopes) => {
  if (Array.isArray(grantedScopes) && grantedScopes.length) {
    return [...grantedScopes].sort((a, b) => a.localeCompare(b));
  }
  return [...API_KEY_SCOPES];
};

export const getBaasWebhookEventOptions = (catalogEvents) => {
  if (Array.isArray(catalogEvents) && catalogEvents.length) {
    return [...catalogEvents].sort((a, b) => a.localeCompare(b));
  }
  return [...WEBHOOK_EVENTS];
};

export const getBaasApiEnvironmentOptions = () => ["production", "sandbox"];
export const getBaasApiPeriodOptions = () => ["24h", "7d", "30d", "90d"];

const ALERT_STORAGE_PREFIX = "baas.api.alertThresholds.v1.";

export const DEFAULT_BAAS_API_ALERT_THRESHOLDS = {
  maxErrorRatePercent: 5,
  maxAvgLatencyMs: 2000,
  enabled: true,
};

export const loadBaasApiAlertThresholds = (instanceId) => {
  if (instanceId == null) return { ...DEFAULT_BAAS_API_ALERT_THRESHOLDS };
  try {
    const raw = localStorage.getItem(`${ALERT_STORAGE_PREFIX}${instanceId}`);
    if (!raw) return { ...DEFAULT_BAAS_API_ALERT_THRESHOLDS };
    const parsed = JSON.parse(raw);
    return {
      maxErrorRatePercent: Number(parsed?.maxErrorRatePercent) || DEFAULT_BAAS_API_ALERT_THRESHOLDS.maxErrorRatePercent,
      maxAvgLatencyMs: Number(parsed?.maxAvgLatencyMs) || DEFAULT_BAAS_API_ALERT_THRESHOLDS.maxAvgLatencyMs,
      enabled: parsed?.enabled !== false,
    };
  } catch {
    return { ...DEFAULT_BAAS_API_ALERT_THRESHOLDS };
  }
};

export const saveBaasApiAlertThresholds = (instanceId, thresholds) => {
  if (instanceId == null) return;
  localStorage.setItem(
    `${ALERT_STORAGE_PREFIX}${instanceId}`,
    JSON.stringify({
      maxErrorRatePercent: Number(thresholds?.maxErrorRatePercent) || DEFAULT_BAAS_API_ALERT_THRESHOLDS.maxErrorRatePercent,
      maxAvgLatencyMs: Number(thresholds?.maxAvgLatencyMs) || DEFAULT_BAAS_API_ALERT_THRESHOLDS.maxAvgLatencyMs,
      enabled: thresholds?.enabled !== false,
    }),
  );
};

export const evaluateBaasApiAlert = (metricsData, thresholds = DEFAULT_BAAS_API_ALERT_THRESHOLDS) => {
  if (!thresholds?.enabled || !metricsData) return false;

  const status =
    metricsData?.health_status ??
    metricsData?.healthStatus ??
    metricsData?.health_summary?.status ??
    metricsData?.healthSummary?.status;
  const incidents =
    metricsData?.health_summary?.incidents ?? metricsData?.healthSummary?.incidents ?? [];
  if (status === "degraded" || status === "down") return true;
  if (Array.isArray(incidents) && incidents.length > 0) return true;

  const errorRate = Number(metricsData?.error_rate ?? metricsData?.errorRate ?? 0);
  const avgLatency = Number(metricsData?.avg_latency_ms ?? metricsData?.avgLatencyMs ?? 0);
  if (errorRate >= Number(thresholds.maxErrorRatePercent || 0)) return true;
  if (avgLatency >= Number(thresholds.maxAvgLatencyMs || 0)) return true;
  return false;
};

export const mapInstanceEnvironmentToFilter = (environment) => {
  const normalized = String(environment || "").trim().toLowerCase();
  if (
    normalized === "development" ||
    normalized === "dev" ||
    normalized === "hml" ||
    normalized === "sandbox"
  ) {
    return "sandbox";
  }
  return "production";
};

const isPortalUnavailable = (error) => {
  const status = Number(error?.status ?? 0);
  return status === 404 || status === 405 || status === 501;
};

const mapAccessInfo = (data, ctx) => ({
  instanceId: Number(data?.baas_instance_id ?? data?.baasInstanceId ?? data?.instance_id ?? data?.instanceId ?? data?.id ?? ctx.instanceId),
  environment: String(data?.environment ?? ctx.environment),
  baasBaseUrl: toPublicBaasApiBaseUrl(data?.baas_base_url ?? data?.baasBaseUrl),
  name: String(data?.name ?? ""),
  active: data?.active !== false,
  programCode: data?.program_code ?? data?.programCode ?? null,
  supportedCurrencies: Array.isArray(data?.supported_currencies)
    ? data.supported_currencies
    : Array.isArray(data?.supportedCurrencies)
      ? data.supportedCurrencies
      : [],
  statusBadge: String(data?.status_badge ?? data?.statusBadge ?? ""),
});

/**
 * Instance metadata for the selected environment.
 * Prefer BaaS GET /v1/instances/{id}; enrich with MobileService (program_code, status, public baas_base_url).
 * @param {object | string} instance
 */
export const fetchBaasApiAccessInfo = async (instance) => {
  const { ctx, base } = instanceBaasPath(instance);

  let baasData = null;
  try {
    baasData = await baasProxy({
      instance,
      method: "GET",
      baasPath: base,
    });
  } catch {
    // Fall through to MobileService enrichment / fallback.
  }

  let mobileData = null;
  try {
    mobileData = await mobileConsoleRequest({
      method: "GET",
      path: `v1/ib/baas/instances/${ctx.instanceId}`,
    });
  } catch {
    // Optional enrichment.
  }

  if (!baasData && !mobileData) {
    throw Object.assign(new Error("Instance access info unavailable"), {
      status: 404,
      code: "instance_not_found",
    });
  }

  const merged = {
    ...(mobileData || {}),
    ...(baasData || {}),
    // Prefer MobileService public baas_base_url (Baas:PublicBaseUrl). BaaS instance
    // detail does not expose it; never display the App Service proxy origin.
    baas_base_url: toPublicBaasApiBaseUrl(
      mobileData?.baas_base_url ??
        mobileData?.baasBaseUrl ??
        baasData?.baas_base_url ??
        baasData?.baasBaseUrl,
    ),
    program_code:
      baasData?.program_code ??
      baasData?.programCode ??
      mobileData?.program_code ??
      mobileData?.programCode ??
      null,
    status_badge:
      baasData?.status_badge ??
      baasData?.statusBadge ??
      mobileData?.status_badge ??
      mobileData?.statusBadge ??
      null,
    environment: ctx.environment,
  };

  return mapAccessInfo(merged, ctx);
};

const mapApiKey = (row) => ({
  id: Number(row?.id ?? row?.api_key_id),
  name: String(row?.name ?? row?.description ?? ""),
  environment: String(row?.environment ?? "production"),
  prefix: String(row?.prefix ?? ""),
  createdAt: row?.created_at ?? row?.createdAt ?? null,
  lastUsedAt: row?.last_used_at ?? row?.lastUsedAt ?? null,
  status: String(row?.status ?? (row?.active === false ? "revoked" : "active")),
});

const mapWebhook = (row) => ({
  id: Number(row?.id ?? row?.webhook_id),
  name: String(row?.name ?? ""),
  url: String(row?.url ?? row?.uri ?? ""),
  secret: row?.secret ? String(row.secret) : "—",
  events: Array.isArray(row?.events) ? row.events : [],
  active: row?.active !== false,
  lastDeliveryAt: row?.last_delivery_at ?? row?.lastDeliveryAt ?? null,
  lastStatus: row?.last_status ?? row?.lastStatus ?? null,
});

const mapApiLogEntry = (row) => ({
  id: Number(row?.id ?? row?.api_events_log_id),
  method: String(row?.method ?? ""),
  path: String(row?.path ?? ""),
  statusCode: row?.status_code ?? row?.statusCode ?? null,
  durationMs: row?.duration_ms ?? row?.durationMs ?? null,
  date: row?.date ?? null,
  correlationId: row?.correlation_id ?? row?.correlationId ?? null,
  /** From telemetry metadata.client (`console` | `carteira`) when tagged by MS proxies. */
  client: row?.client ? String(row.client) : null,
});

/** BaaS path prefix for instance portal resources. */
const instanceBaasPath = (instance) => {
  const ctx = resolveBaasProxyContext(instance);
  return { ctx, base: `v1/instances/${ctx.instanceId}` };
};

const portalPath = (instanceId, suffix) =>
  `v1/ib/baas/instances/${instanceId}/${suffix}`;

/**
 * Telemetry / webhook rows live in the lane DB (Prod vs Dev).
 * MobileService fallback only reads Prod SQL — never use it for HML cards
 * (would mix Prod instance data when HmlInstanceId collides with a Prod id).
 * Keys/scopes stay on the Prod catalog and may still fall back.
 */
const isLaneScopedPortalSuffix = (baasSuffix) => {
  const suffix = String(baasSuffix || "").toLowerCase();
  return (
    suffix === "api-logs" ||
    suffix === "api-metrics" ||
    suffix === "pix-metrics" ||
    suffix === "webhooks" ||
    suffix.startsWith("webhooks/")
  );
};

/**
 * Try BaaS via console proxy; on portal 404 fall back to MobileService Prod SQL
 * (Prod catalog / Prod lane only — skipped for HML lane-scoped resources).
 * @param {object | string} instance
 * @param {string} baasSuffix e.g. "webhooks"
 * @param {{ query?: object }} [options]
 */
const fetchPortalResource = async (instance, baasSuffix, options = {}) => {
  const { ctx, base } = instanceBaasPath(instance);
  const skipProdSqlFallback =
    isBaasHmlInstance(instance) && isLaneScopedPortalSuffix(baasSuffix);

  try {
    return {
      data: await baasProxy({
        instance,
        method: "GET",
        baasPath: `${base}/${baasSuffix}`,
        query: options.query,
      }),
      source: "baas",
    };
  } catch (error) {
    if (skipProdSqlFallback) {
      throw error;
    }

    if (!isPortalUnavailable(error)) {
      // Non-404: still try local fallback (membership-safe SQL).
    }

    try {
      return {
        data: await mobileConsoleRequest({
          method: "GET",
          path: portalPath(ctx.instanceId, baasSuffix),
          query: options.query,
        }),
        source: "mobile",
      };
    } catch (fallbackError) {
      if (isPortalUnavailable(error)) {
        throw fallbackError;
      }
      throw error;
    }
  }
};

/**
 * Granted API scopes for the instance partner.
 * Prefer BaaS via console proxy; fall back to MobileService Prod DB listing.
 * @param {object | string} instance
 */
export const fetchBaasInstanceScopes = async (instance) => {
  try {
    const { data } = await fetchPortalResource(instance, "scopes");
    return {
      scopes: Array.isArray(data?.scopes) ? data.scopes : [],
      apiPartnerId: data?.api_partner_id ?? data?.apiPartnerId ?? null,
      error: false,
    };
  } catch {
    return { scopes: [], apiPartnerId: null, error: true };
  }
};

/**
 * @param {object | string} instance
 * @returns {Promise<{ items: Array, manageSupported: boolean }>}
 */
export const fetchBaasApiKeys = async (instance) => {
  try {
    // Prefer BaaS portal so manage_supported=true when writes are available.
    // MobileService fallback lists keys but cannot create/revoke.
    const { data, source } = await fetchPortalResource(instance, "api-keys");
    return {
      items: Array.isArray(data?.items) ? data.items.map(mapApiKey) : [],
      manageSupported:
        source === "baas"
          ? Boolean(data?.manage_supported ?? data?.manageSupported ?? true)
          : Boolean(data?.manage_supported ?? data?.manageSupported ?? false),
    };
  } catch {
    return { items: [], manageSupported: false };
  }
};

/**
 * @param {object | string} instance
 * @returns {Promise<{ items: Array, manageSupported: boolean }>}
 */
export const fetchBaasWebhooks = async (instance) => {
  try {
    const { data, source } = await fetchPortalResource(instance, "webhooks");
    return {
      items: Array.isArray(data?.items) ? data.items.map(mapWebhook) : [],
      manageSupported:
        source === "baas"
          ? Boolean(data?.manage_supported ?? data?.manageSupported ?? true)
          : false,
    };
  } catch {
    return { items: [], manageSupported: false };
  }
};

/**
 * Creates a partner API key for the instance.
 * New keys inherit the instance ApiPartner scopes (BaaS ignores per-key scopes).
 * @param {object | string} instance
 * @param {{ name: string, environment?: "sandbox"|"production"|"dev"|"prod" }} payload
 */
export const createBaasApiKey = async (instance, payload) => {
  const { base } = instanceBaasPath(instance);
  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `${base}/api-keys`,
    body: {
      name: payload?.name,
      environment: payload?.environment,
    },
  });

  return {
    ...mapApiKey(data),
    fullKey: data?.full_key ?? data?.fullKey ?? null,
  };
};

/** @param {object | string} instance @param {number|string} keyId */
export const revokeBaasApiKey = async (instance, keyId) => {
  const { base } = instanceBaasPath(instance);
  await baasProxy({
    instance,
    method: "DELETE",
    baasPath: `${base}/api-keys/${keyId}`,
  });
};

/**
 * Reveal full partner API key (admin console). List never returns full_key.
 * Requires BaaS scope instances.api_keys.read; FE gates to instance/global admin.
 * @param {object | string} instance
 * @param {number|string} keyId
 */
export const revealBaasApiKey = async (instance, keyId) => {
  const { base } = instanceBaasPath(instance);
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `${base}/api-keys/${keyId}`,
  });

  return {
    ...mapApiKey(data),
    fullKey: data?.full_key ?? data?.fullKey ?? null,
  };
};

/**
 * Catalog of webhook event names for the create modal.
 * Prefer BaaS portal; fall back to the static baas catalog list.
 * @param {object | string} instance
 */
export const fetchBaasWebhookEventCatalog = async (instance) => {
  try {
    const { data } = await fetchPortalResource(instance, "webhook-events");
    const events = Array.isArray(data?.events)
      ? data.events
      : Array.isArray(data?.items)
        ? data.items
        : [];
    const names = events
      .map((item) => (typeof item === "string" ? item : item?.event_name ?? item?.eventName ?? item?.name))
      .filter(Boolean)
      .map(String);
    return names.length ? names : [...WEBHOOK_EVENTS];
  } catch {
    return [...WEBHOOK_EVENTS];
  }
};

/**
 * @param {object | string} instance
 * @param {{ name: string, url: string, active?: boolean, events: string[] }} payload
 */
export const createBaasWebhook = async (instance, payload) => {
  const { base } = instanceBaasPath(instance);
  await baasProxy({
    instance,
    method: "POST",
    baasPath: `${base}/webhooks`,
    body: {
      name: payload?.name,
      url: payload?.url,
      active: payload?.active,
      events: payload?.events,
    },
  });
};

/** @param {object | string} instance @param {number|string} webhookId @param {boolean} active */
export const toggleBaasWebhook = async (instance, webhookId, active) => {
  const { base } = instanceBaasPath(instance);
  await baasProxy({
    instance,
    method: "PATCH",
    baasPath: `${base}/webhooks/${webhookId}`,
    body: { active },
  });
};

/** @param {object | string} instance @param {number|string} webhookId */
export const testBaasWebhook = async (instance, webhookId) => {
  const { base } = instanceBaasPath(instance);
  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `${base}/webhooks/${webhookId}/test`,
  });

  return {
    statusCode: Number(data?.status_code ?? data?.statusCode ?? 0),
    latencyMs: Number(data?.latency_ms ?? data?.latencyMs ?? 0),
  };
};

const mapWebhookDelivery = (row) => ({
  id: Number(row?.id ?? row?.webhook_event_log_id),
  eventType: String(row?.event_type ?? row?.eventType ?? ""),
  eventId: String(row?.event_id ?? row?.eventId ?? ""),
  status: String(row?.status ?? "failed"),
  httpStatusCode: row?.http_status_code ?? row?.httpStatusCode ?? null,
  retryCount: Number(row?.retry_count ?? row?.retryCount ?? 0),
  correlationId: row?.correlation_id ?? row?.correlationId ?? null,
  errorMessage: row?.error_message ?? row?.errorMessage ?? null,
  createdAt: row?.created_at ?? row?.createdAt ?? null,
  deliveredAt: row?.delivered_at ?? row?.deliveredAt ?? null,
});

/**
 * Delivery history for a webhook subscription group.
 * Prefer BaaS via console proxy; fall back to MobileService Prod SQL.
 * @param {object | string} instance
 * @param {number|string} webhookId
 * @param {{ page?: number, pageSize?: number }} [options]
 */
export const fetchBaasWebhookDeliveries = async (instance, webhookId, options = {}) => {
  const query = {};
  if (options?.page) query.page = options.page;
  if (options?.pageSize) query.page_size = options.pageSize;

  try {
    const { data } = await fetchPortalResource(
      instance,
      `webhooks/${webhookId}/deliveries`,
      { query: Object.keys(query).length ? query : undefined },
    );

    return {
      items: Array.isArray(data?.items) ? data.items.map(mapWebhookDelivery) : [],
      page: Number(data?.page ?? 1),
      pageSize: Number(data?.page_size ?? data?.pageSize ?? 25),
      totalCount: Number(data?.total_count ?? data?.totalCount ?? 0),
      error: false,
    };
  } catch {
    return { items: [], page: 1, pageSize: 25, totalCount: 0, error: true };
  }
};

/**
 * @param {object | string} instance
 * @param {{ period?: string }} [options]
 */
export const fetchBaasApiMetrics = async (instance, options = {}) => {
  const query = options?.period ? { period: options.period } : undefined;
  const { data } = await fetchPortalResource(instance, "api-metrics", { query });
  return data;
};

const mapPixMetricsPayload = (data, options = {}) => {
  const volumeOverTime = data?.volume_over_time ?? data?.volumeOverTime ?? null;
  const values = Array.isArray(volumeOverTime?.values)
    ? volumeOverTime.values.map((value) => Number(value) || 0)
    : [];

  return {
    period: String(data?.period ?? options?.period ?? "24h"),
    currency: String(data?.currency ?? "BRL"),
    volume: Number(data?.volume ?? 0),
    txCount: Number(data?.tx_count ?? data?.txCount ?? 0),
    successRate: Number(data?.success_rate ?? data?.successRate ?? 100),
    sentCount: Number(data?.sent_count ?? data?.sentCount ?? 0),
    receivedCount: Number(data?.received_count ?? data?.receivedCount ?? 0),
    failedCount: Number(data?.failed_count ?? data?.failedCount ?? 0),
    sentVolume: Number(data?.sent_volume ?? data?.sentVolume ?? 0),
    receivedVolume: Number(data?.received_volume ?? data?.receivedVolume ?? 0),
    trendVolume: values,
    volumeOverTime: {
      labels: Array.isArray(volumeOverTime?.labels) ? volumeOverTime.labels : [],
      values,
    },
  };
};

/**
 * Pix home-rail metrics (volume / tx / success / sent-received-failed).
 * Prefer BaaS via console proxy; fall back to MobileService Prod SQL.
 * @param {object | string} instance
 * @param {{ period?: string, date_from?: string, date_to?: string }} [options]
 */
export const fetchBaasPixMetrics = async (instance, options = {}) => {
  const query = {};
  if (options?.period) query.period = options.period;
  if (options?.date_from) query.date_from = options.date_from;
  if (options?.date_to) query.date_to = options.date_to;
  const { data } = await fetchPortalResource(instance, "pix-metrics", {
    query: Object.keys(query).length ? query : undefined,
  });
  return mapPixMetricsPayload(data, options);
};

/**
 * Valut matrix only: GET `v1/instances/peer-pix-metrics` — Pix volume across
 * every active instance (or one tenant when instanceId is set). Empty for partners.
 */
export const fetchBaasPeerPixMetrics = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) {
    return mapPixMetricsPayload(null, options);
  }

  try {
    const query = {};
    if (options?.period) query.period = options.period;
    if (options?.date_from) query.date_from = options.date_from;
    if (options?.date_to) query.date_to = options.date_to;
    if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
      query.instance_id = Number(options.instanceId);
    }
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "v1/instances/peer-pix-metrics",
      query: Object.keys(query).length ? query : undefined,
    });
    return mapPixMetricsPayload(data, options);
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/instances/peer-pix-metrics failed", error);
    }
    return mapPixMetricsPayload(null, options);
  }
};

/**
 * @param {object | string} instance
 * @param {{ period?: string, page?: number, pageSize?: number }} [options]
 */
export const fetchBaasApiLogs = async (instance, options = {}) => {
  const query = {};
  if (options?.period) query.period = options.period;
  if (options?.page) query.page = options.page;
  if (options?.pageSize) query.page_size = options.pageSize;

  const { data } = await fetchPortalResource(instance, "api-logs", {
    query: Object.keys(query).length ? query : undefined,
  });

  return {
    items: Array.isArray(data?.items) ? data.items.map(mapApiLogEntry) : [],
    page: Number(data?.page ?? 1),
    pageSize: Number(data?.page_size ?? data?.pageSize ?? 25),
    totalCount: Number(data?.total_count ?? data?.totalCount ?? 0),
    totals: data?.totals ?? null,
  };
};
